/**
 * `ChecksSection` — the Discoverable + Policies & records cards.
 *
 * Each section renders:
 *   - A header with the serif title and a one-line "what this measures" sub.
 *   - One row per failing check — 6px dot (coral=block, amber=warn), a fact
 *     headline, a muted evidence line, and one verb button.
 *   - Row with the agent card + llms.txt summary (Discoverable only).
 *   - A `<details>` "N checks passing" at the bottom; expands into muted
 *     rows with ✓ marker, label, plain result, and an "Evidence" link that
 *     calls `onOpenCheck(check_id)`.
 *   - A muted "Your next step, above." line for the hero's issue (that row
 *     renders in exactly one place — no duplication).
 */

import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { ReactNode } from 'react';
import type {
  ReadinessCheck,
  ReadinessCheckGroup,
} from '@sill/types';

import { useC } from '../../lib/theme.js';
import { monoFont, sansFont, serifFont } from './fonts.js';
import { MonoLink } from './MonoLink.js';

export interface ChecksSectionProps {
  readonly id: string;
  readonly group: ReadinessCheckGroup;
  readonly title: string;
  readonly subtitle: string;
  readonly checks: readonly ReadinessCheck[];
  /** Check id that is the hero's next step — rendered as the muted "above" line, not a row. */
  readonly promoted_check_id: string | null;
  readonly onAction: (check: ReadinessCheck) => void;
  readonly onEvidence: (check_id: string) => void;
  readonly extra?: ReactNode;
  /** Check ids to omit from BOTH the fail-row list and the collapsed
   *  passing-row list — used when another UI element on the page owns
   *  that row (e.g. the Agent card + llms.txt editable pair). */
  readonly hiddenCheckIds?: ReadonlySet<string>;
  /** step_ids the merchant has "Not now"'d. The row is still rendered
   *  in the fail list, but is muted and carries an "Undo" affordance;
   *  the hero skips these (they are off the ranker's rotation). */
  readonly dismissedStepIds?: ReadonlySet<string>;
  readonly onUndismiss?: (step_id: string) => void;
  /** Inline URL-add on a `policy.*_missing` row (ToS / privacy / returns). */
  readonly onAddUrl?: (check: ReadinessCheck, url: string) => Promise<void>;
  /** Inline suggestion-apply on a `card.field:*` row (one click sets
   *  the suggested value). */
  readonly onApplySuggestion?: (check: ReadinessCheck) => Promise<void>;
  /** The site's domain — used to compute per-field policy-row
   *  placeholders ("https://<domain>/privacy" vs ".../returns"). */
  readonly domain?: string;
  /** Suggested-URL map (from site scan) keyed by step_id — prefilled
   *  into the inline URL input when present. */
  readonly suggestedPolicyUrlFor?: (step_id: string) => string | null;
  /** Passing-check facts: optional per-check extra lines shown
   *  underneath the headline inside the collapsed "N checks passing"
   *  list. Returning `null` falls back to the plain evidence line. */
  readonly passingExtra?: (check: ReadinessCheck) => ReactNode | null;
  /** TRUE while at least one network source the group reads is still
   *  loading — surfaces a quiet "Checking…" row at the top of the
   *  section so the merchant sees that the counts below may still
   *  change. No dot, no verb, muted color. */
  readonly pending_signals?: boolean;
  /** Inline edit for a passing policy row (terms, privacy, returns URL,
   *  return window) — returns a React node to render in the right-aligned
   *  "trailing link" column. When provided for a check, replaces the
   *  default "Evidence" link for that row. */
  readonly passingInlineEdit?: (check: ReadinessCheck) => ReactNode | null;
  /** Trailing link for a passing row (e.g. "View records") — rendered in
   *  the same right-aligned column as "Evidence", replacing it. */
  readonly passingAction?: (check: ReadinessCheck) => ReactNode | null;
}

function DotColor(severity: 'block' | 'warn'): 'coral' | 'amber' {
  return severity === 'block' ? 'coral' : 'amber';
}

export function ChecksSection({
  id, group, title, subtitle, checks, promoted_check_id, onAction, onEvidence, extra, hiddenCheckIds,
  dismissedStepIds, onUndismiss, onAddUrl, onApplySuggestion,
  domain, suggestedPolicyUrlFor, passingExtra, passingAction, pending_signals = false,
  passingInlineEdit,
}: ChecksSectionProps): JSX.Element {
  const C = useC();
  const hidden = hiddenCheckIds ?? new Set<string>();
  const dismissed = dismissedStepIds ?? new Set<string>();
  const groupChecks = checks.filter((c) => c.group === group && !hidden.has(c.check_id));
  const fails = groupChecks.filter((c) => c.state === 'fail');
  const passes = groupChecks.filter((c) => c.state === 'pass' || c.state === 'na' || (c.state === 'unknown' && c.uncounted));

  const [showPasses, setShowPasses] = useState(false);
  const promoted = promoted_check_id !== null ? fails.find((c) => c.check_id === promoted_check_id) ?? null : null;
  const nonPromotedFails = fails.filter((c) => c.check_id !== (promoted?.check_id ?? ''));

  return (
    <section
      id={id}
      aria-label={title}
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 4,
        fontFamily: sansFont,
      }}
    >
      <div
        className="flex items-baseline justify-between gap-3"
        style={{ padding: '24px 24px 16px' }}
      >
        <h2 style={{ margin: 0, fontFamily: serifFont, fontWeight: 400, fontSize: 'clamp(22px, 3vw, 28px)', color: C.text }}>
          {title}
        </h2>
        <div style={{ fontSize: 12, color: C.muted }}>{subtitle}</div>
      </div>

      {pending_signals ? (
        <div
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            padding: '14px 24px',
            borderTop: `1px solid ${C.borderSoft}`,
            fontSize: 13,
            color: C.muted,
            fontStyle: 'italic',
          }}
        >
          Checking…
        </div>
      ) : null}

      {promoted !== null ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          padding: '18px 24px',
          borderTop: `1px solid ${C.borderSoft}`,
          boxShadow: `inset 2px 0 0 ${promoted.severity === 'block' ? C.coral : C.amber}`,
        }}>
          <span
            aria-hidden="true"
            style={{ width: 6, height: 6, borderRadius: '50%', background: promoted.severity === 'block' ? C.coral : C.amber, flexShrink: 0 }}
          />
          <div style={{ flexGrow: 1 }}>
            <div style={{ fontSize: 15, color: C.text }}>{promoted.headline}</div>
            <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>This is your next step, above.</div>
          </div>
          <MonoLink onClick={() => onEvidence(promoted.check_id)} aria-label="See evidence">Evidence</MonoLink>
        </div>
      ) : null}

      {nonPromotedFails.map((c) => (
        <FailRow
          key={c.check_id}
          C={C}
          check={c}
          snoozed={c.step_id !== null && dismissed.has(c.step_id)}
          onAction={() => onAction(c)}
          onEvidence={() => onEvidence(c.check_id)}
          onUndismiss={onUndismiss !== undefined && c.step_id !== null
            ? () => onUndismiss(c.step_id!)
            : undefined}
          onAddUrl={onAddUrl !== undefined
            ? async (url) => { await onAddUrl(c, url); }
            : undefined}
          onApplySuggestion={onApplySuggestion !== undefined
            ? async () => { await onApplySuggestion(c); }
            : undefined}
          domain={domain}
          suggestedUrl={
            suggestedPolicyUrlFor !== undefined && c.step_id !== null
              ? suggestedPolicyUrlFor(c.step_id)
              : null
          }
        />
      ))}

      {extra !== undefined ? extra : null}

      {passes.length > 0 ? (
        <details
          style={{ borderTop: `1px solid ${C.borderSoft}` }}
          onToggle={(e) => setShowPasses((e.target as HTMLDetailsElement).open)}
        >
          <summary
            style={{
              padding: '14px 24px',
              fontSize: 14,
              color: C.body,
              cursor: 'pointer',
              listStyle: 'none',
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              fontFamily: sansFont,
              minHeight: 44,
            }}
          >
            <ChevronDown
              size={12}
              color={C.muted}
              style={{ transform: showPasses ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms ease' }}
              aria-hidden="true"
            />
            <span aria-hidden="true" style={{ color: C.mintFg, fontFamily: monoFont, fontSize: 12 }}>✓</span>
            {group === 'policies' ? `${passes.length} in place` : `${passes.length} checks passing`}
          </summary>
          <div>
            {passes.map((c) => {
              const extraNode = passingExtra !== undefined ? passingExtra(c) : null;
              const inlineEditNode = passingInlineEdit !== undefined ? passingInlineEdit(c) : null;
              // When the caller provides a rich extra (e.g. the
              // Guardrails row with its rule-name list + "Open
              // Guardrails" link), the row's own "Evidence" affordance
              // becomes redundant — the inline link does the same
              // navigation. Hide Evidence in that case so we don't
              // end up with two controls (item 5 invariant).
              const hasInlineLink = extraNode !== null;
              // If an inline-edit control is provided, it replaces the
              // "Evidence" link in the trailing right-aligned column.
              const actionNode = passingAction !== undefined ? passingAction(c) : null;
              const trailing: ReactNode = inlineEditNode !== null
                ? inlineEditNode
                : actionNode !== null
                  ? actionNode
                  : !hasInlineLink && (c.state === 'pass' || c.state === 'unknown')
                  ? <MonoLink onClick={() => onEvidence(c.check_id)} aria-label="Evidence">Evidence</MonoLink>
                  : <span />;
              // When an inline-edit is rendered, we show it inside the
              // right-aligned column (no separate evidence line) and
              // drop the middle "value" cell so the input is wider.
              if (inlineEditNode !== null) {
                return (
                  <div
                    key={c.check_id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '20px minmax(140px, 220px) 1fr',
                      gap: 12,
                      alignItems: 'center',
                      padding: '14px 24px',
                      fontSize: 13,
                      color: C.muted,
                      borderTop: `1px solid ${C.borderSoft}`,
                      fontFamily: sansFont,
                    }}
                  >
                    <span aria-hidden="true" style={{ color: C.mintFg, fontFamily: monoFont }}>
                      {c.state === 'na' ? '–' : '✓'}
                    </span>
                    <span style={{ fontWeight: 400, color: C.body }}>{c.headline.replace(/\.$/, '')}</span>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {inlineEditNode}
                    </div>
                  </div>
                );
              }
              return (
              <div
                key={c.check_id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '20px minmax(140px, 220px) 1fr auto',
                  gap: 12,
                  alignItems: 'center',
                  padding: '10px 24px',
                  fontSize: 13,
                  color: C.muted,
                  borderTop: `1px solid ${C.borderSoft}`,
                  fontFamily: sansFont,
                }}
              >
                <span aria-hidden="true" style={{ color: C.mintFg, fontFamily: monoFont }}>
                  {c.state === 'na' ? '–' : '✓'}
                </span>
                <span style={{ fontWeight: 400, color: C.body }}>{c.headline.replace(/\.$/, '')}</span>
                <span>{extraNode !== null ? extraNode : c.evidence}</span>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{trailing}</div>
              </div>
            );
            })}
          </div>
        </details>
      ) : null}
    </section>
  );
}


/**
 * `FailRow` — a single failing-check row with inline fix affordances.
 *
 * Four shapes:
 *   - a merchant has snoozed the step → muted row with "Snoozed · Undo".
 *   - a `policy.*_missing` row with `onAddUrl` wired → inline URL input +
 *     Save (secondary "Show me how" opens the drawer for the step-by-step).
 *   - a `card.field:*` row with a suggested value → one-click "Use <value>".
 *     The parent handler goes through the typed agent-card-profile write
 *     path; a merchant click is the write authority.
 *   - everything else → the plain `c.action.label` button.
 */
interface FailRowProps {
  readonly C: ReturnType<typeof useC>;
  readonly check: ReadinessCheck;
  readonly snoozed: boolean;
  readonly onAction: () => void;
  readonly onEvidence: () => void;
  readonly onUndismiss?: (() => void) | undefined;
  readonly onAddUrl?: ((url: string) => Promise<void>) | undefined;
  readonly onApplySuggestion?: (() => Promise<void>) | undefined;
  readonly domain?: string | undefined;
  readonly suggestedUrl?: string | null | undefined;
}

/** Per-field placeholder for a policy URL row — real domain + kind. */
function policyPlaceholder(step_id: string | null, domain: string | undefined): string {
  const base = `https://${domain ?? 'your-site.com'}`;
  if (step_id === 'policy.privacy_missing') return `${base}/privacy`;
  if (step_id === 'policy.tos_missing') return `${base}/terms`;
  return `${base}/returns`;
}

function FailRow({
  C, check, snoozed, onAction, onEvidence, onUndismiss, onAddUrl, onApplySuggestion,
  domain, suggestedUrl,
}: FailRowProps): JSX.Element {
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bg = check.severity === 'block' ? C.coral : C.amber;
  const isPolicyUrl = check.step_id?.startsWith('policy.') === true
    && check.step_id?.endsWith('_missing') === true;
  const isCardField = check.step_id?.startsWith('card.field:') === true;
  const isUseValue = check.action?.verb === 'use_value';
  // "show_me_how" rows have ONE action (the Show-me-how button). Rows
  // with any other inline action (use_value, add_url, turn_on, …) get
  // the inline action + an Evidence link. This is the item-5 invariant:
  // never two controls that open the same drawer.
  const isShowMeHow = check.action?.verb === 'show_me_how';

  // Prefer the explicit prop (per-field suggested URL from the scan,
  // keyed on step_id by the parent). Fall back to anything the pure
  // evidence sentence embedded so the component still works if the
  // caller didn't pipe `suggestedPolicyUrlFor`.
  const suggestedUrlFromEvidence = (() => {
    if (!isPolicyUrl) return '';
    if (suggestedUrl !== null && suggestedUrl !== undefined && suggestedUrl.length > 0) return suggestedUrl;
    const m = check.evidence.match(/https?:\/\/\S+/);
    return m?.[0] ?? '';
  })();
  const [url, setUrl] = useState(suggestedUrlFromEvidence);
  // Keep in sync when the evidence changes (next refresh produces a
  // new suggestion / clears it).
  useEffect(() => {
    setUrl(suggestedUrlFromEvidence);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check.evidence, suggestedUrl]);

  async function handleApply(): Promise<void> {
    if (applying || onApplySuggestion === undefined) return;
    setApplying(true);
    setError(null);
    try {
      await onApplySuggestion();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save. Try again.');
    } finally {
      setApplying(false);
    }
  }

  async function handleAddUrl(): Promise<void> {
    if (applying || onAddUrl === undefined) return;
    const trimmed = url.trim();
    if (trimmed.length === 0) return;
    setApplying(true);
    setError(null);
    try {
      await onAddUrl(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save. Try again.');
    } finally {
      setApplying(false);
    }
  }

  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 18,
        padding: '18px 24px', borderTop: `1px solid ${C.borderSoft}`,
        opacity: snoozed ? 0.6 : 1,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6, height: 6, borderRadius: '50%',
          background: snoozed ? C.dim : bg,
          flexShrink: 0,
          marginTop: 7,
        }}
      />
      <div style={{ flexGrow: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, color: C.text }}>{check.headline}</div>
        {check.evidence !== '' ? (
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
            {check.evidence}
            {/*
              Item 5: a failing row has ONE action.
              - Rows with inline fixes (use_value, add_url, turn_on) ALSO
                get a quiet Evidence link (two different drawers).
              - Rows with the "Show me how" button get NO Evidence link
                (both would open the same drawer).
            */}
            {!isShowMeHow ? (
              <>
                {' '}
                <MonoLink onClick={onEvidence} aria-label="Evidence">Evidence</MonoLink>
              </>
            ) : null}
          </div>
        ) : null}
        {error !== null ? (
          <div
            role="alert"
            style={{
              fontSize: 12, color: C.coral, marginTop: 6,
              fontFamily: sansFont,
            }}
          >
            {error}
          </div>
        ) : null}
        {!snoozed && isPolicyUrl && onAddUrl !== undefined ? (
          <form
            onSubmit={(e) => { e.preventDefault(); void handleAddUrl(); }}
            style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
          >
            <input
              type="url"
              inputMode="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={policyPlaceholder(check.step_id, domain)}
              disabled={applying}
              aria-label={
                check.step_id === 'policy.privacy_missing' ? 'Privacy policy URL'
                : check.step_id === 'policy.tos_missing' ? 'Terms of service URL'
                : 'Returns policy URL'
              }
              style={{
                flex: '1 1 240px',
                minWidth: 180,
                fontFamily: monoFont, fontSize: 12,
                padding: '8px 10px',
                background: C.bg, color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 3,
              }}
            />
            <button
              type="submit"
              disabled={applying || url.trim().length === 0}
              style={{
                fontFamily: sansFont, fontSize: 13, fontWeight: 500,
                padding: '7px 14px', minHeight: 36,
                background: 'transparent', color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 3,
                cursor: applying ? 'default' : 'pointer',
                opacity: applying ? 0.6 : 1,
              }}
            >
              {applying ? 'Saving…' : 'Save'}
            </button>
            {/* No "Show me how" twin here — Save IS the fix.
                (Round 3: don't double-link the same drawer.) */}
          </form>
        ) : null}
      </div>
      {snoozed ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: '0.06em' }}>
            SNOOZED
          </span>
          {onUndismiss !== undefined ? (
            <MonoLink onClick={onUndismiss} aria-label="Undo snooze">Undo</MonoLink>
          ) : null}
        </div>
      ) : isCardField && isUseValue && onApplySuggestion !== undefined ? (
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => void handleApply()}
            disabled={applying}
            style={{
              fontFamily: sansFont, fontSize: 13, padding: '7px 14px', minHeight: 44,
              background: 'transparent', color: C.text, border: `1px solid ${C.border}`, borderRadius: 3,
              cursor: applying ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
              opacity: applying ? 0.7 : 1,
            }}
          >
            {applying ? 'Saving…' : (check.action?.label ?? 'Use value')}
          </button>
          <button
            type="button"
            onClick={onAction}
            style={{
              fontFamily: sansFont, fontSize: 13, padding: '7px 14px', minHeight: 44,
              background: 'transparent', color: C.text, border: `1px solid ${C.border}`, borderRadius: 3,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
            aria-label="Edit value"
          >
            Edit
          </button>
        </div>
      ) : !isPolicyUrl && check.action !== null ? (
        <button
          type="button"
          onClick={onAction}
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            padding: '7px 14px',
            minHeight: 44,
            background: 'transparent',
            color: C.text,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {check.action.label}
        </button>
      ) : null}
    </div>
  );
}
